import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Box,
	Button,
	Checkbox,
	Divider,
	FormControl,
	HStack,
	Icon,
	Input,
	Radio,
	RadioGroup,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tooltip,
	Tr,
	VStack,
	Wrap,
	WrapItem,
	useColorModeValue,
	useDisclosure,
	useToast,
} from "@chakra-ui/react";
import { Select as ChakraSelect } from "chakra-react-select";
import moment from "moment";
import {
	FiCalendar,
	FiFile,
	FiFileText,
	FiGlobe,
	FiHash,
	FiInfo,
	FiMail,
	FiPhone,
	FiSearch,
	FiSettings,
	FiTrendingUp,
	FiUpload,
	FiUser,
	FiUsers,
} from "react-icons/fi";

import {
	addDocumentAsync,
	addLeaseContactAsync,
	addReportAsync,
	addTenantAsync,
	selectActiveProperties,
	updateOwnerAsync,
	updateTenantFormerAsync,
} from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import { additionalLeaseDocumentTypes, leaseDocumentTypeIds, reportTypes } from "../../utils/types";
import { addDocumentVerifier, addLeaseContactVerifier, addLeaseVerifier, addTenantVerifier, getPropertyStatus } from "../../utils/verifier";
import ContactPopover from "../ContactPopover/ContactPopover";
import TenantPopover from "../ContactPopover/TenantPopover";
import Dropzone from "../Dropzone/Dropzone";
import LeaseTableRow from "../TableRows/LeaseTableRow";
import ReportsTableRow from "../TableRows/ReportsTableRow";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "./shared";
function mergeAndDeduplicateObjects(arr) {
	const uniqueObjects = new Map();
	arr.forEach((item) => {
		if (!uniqueObjects.has(item.id)) {
			uniqueObjects.set(item.id, item);
		}
	});
	return [...uniqueObjects.values()];
}

export default function TenantsTab({ property }) {
	const isArchived = property.archived;
	const initialDocumentInformation = {
		startingDate: "",
		expirationDate: "",
		documentNumber: "",
		documentType: "lease",
		documentData: "",
		propertyId: property.id,
		documentName: "",
		contentType: "",
		amount: "",
		currency: "ron",
		firstRentDay: "",
		lastRentDay: "",
		exchangeRateType: "bnr",
		active: true,
	};
	const initialAdditionalDocumentInformation = {
		startingDate: "",
		expirationDate: "",
		documentNumber: "",
		documentType: "",
		documentData: "",
		propertyId: property.id,
		documentName: "",
		contentType: "",
		active: true,
	};
	const initialTenantInformation = {
		name: "",
		email: "",
		phoneNumber: "",
		propertyId: property.id,
		contactType: "tenant",
		existing: false,
	};
	const initialContactInformation = {
		name: "",
		email: "",
		phoneNumber: "",
		propertyId: property.id,
		contactType: "occupant",
	};
	const contactTypes = [
		{ id: "legalPerson", label: "Persoana Juridica" },
		{ id: "contactPerson", label: "Persoana de contact" },
		{ id: "occupant", label: "Occupant" },
		{ id: "accountant", label: "Accountant" },
		{ id: "tenantFather", label: "Tenant Father" },
		{ id: "tenantMother", label: "Tenant Mother" },
		{ id: "tenantSon", label: "Tenant Son" },
		{ id: "tenantDaughter", label: "Tenant Daughter" },
		{ id: "companyAdministrator", label: "Company Administrator" },
		{ id: "formerTenant", label: "Fost chirias" },
		{ id: "other", label: "Other" },
	];
	const dispatch = useDispatch();
	const { t } = useTranslation();

	// Color mode variables
	const borderColor = useColorModeValue("gray.200", "gray.700");

	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isAdditionalDocumentOpen, onOpen: onAdditionalDocumentOpen, onClose: onAdditionalDocumentClose } = useDisclosure();
	const { isOpen: isOpenAddTenant, onOpen: onOpenAddTenant, onClose: onCloseAddTenant } = useDisclosure();
	const { isOpen: isOpenAddContact, onOpen: onOpenAddContact, onClose: onCloseAddContact } = useDisclosure();
	const { isOpen: isReportOpen, onOpen: onReportOpen, onClose: onReportClose } = useDisclosure();
	const { isOpen: isExistingTenantOpen, onToggle: onExistingTenantToggle } = useDisclosure();

	const ownerBillingInformation = property.owner.billingInformation;
	const properties = useSelector(selectActiveProperties);
	const uniqueTenantsAndFormerTenants = properties.reduce((acc, property) => {
		const allPeople = [...property.tenants, ...property.formerTenants];
		return mergeAndDeduplicateObjects([...acc, ...allPeople]);
	}, []);
	const user = useSelector((state) => state.user.user);
	const agencyBillingInformation = user.billingInformation;
	// const formerTenants = property.tenants.filter((t) => t.former);
	const formerTenants = property.formerTenants;
	const formerContacts = property.leaseContacts.filter((t) => t.former);
	const initialReportInformation = {
		propertyId: property.id,
		addedDate: moment().format("yyyy-MM-DD"),
		documentNumber: "",
		documentType: "",
		documentData: "",
		documentName: "",
		contentType: "",
		media: [],
		description: "",
	};
	const [reportInformation, setReportInformation] = useState(initialReportInformation);

	const [additionalDocumentInformation, setAdditionalDocumentInformation] = useState(initialAdditionalDocumentInformation);
	const [documentInformation, setDocumentInformation] = useState(initialDocumentInformation);
	const [tenantInformation, setTenantInformation] = useState(initialTenantInformation);
	const [contactInformation, setContactInformation] = useState(initialContactInformation);
	const [error, setError] = useState({});
	const toast = useToast();
	const reports = property.reports;
	const documents = property.documents.filter((d) => leaseDocumentTypeIds.includes(d.type));
	const handleAddReportChange = (e) => {
		setReportInformation({ ...reportInformation, [e.target.name]: e.target.value });
	};
	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const buffer = await acceptedFiles[0].arrayBuffer();
		const documentName = acceptedFiles[0].name;
		const contentType = acceptedFiles[0].type;
		setDocumentInformation({ ...documentInformation, documentData: _arrayBufferToBase64(buffer), documentName, contentType });
	};
	const onAdditionalDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const buffer = await acceptedFiles[0].arrayBuffer();
		const documentName = acceptedFiles[0].name;
		const contentType = acceptedFiles[0].type;
		setAdditionalDocumentInformation({ ...additionalDocumentInformation, documentData: _arrayBufferToBase64(buffer), documentName, contentType });
	};
	const addDocument = () => {
		console.log(documentInformation);
		const errors = addLeaseVerifier(documentInformation);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});

		dispatch(addDocumentAsync(documentInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Document Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setDocumentInformation(initialDocumentInformation);
				onClose();
			});
	};
	const addAdditionalDocument = () => {
		console.log(additionalDocumentInformation);
		const errors = addDocumentVerifier(additionalDocumentInformation);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addDocumentAsync(additionalDocumentInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Document Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setAdditionalDocumentInformation(initialAdditionalDocumentInformation);
				onAdditionalDocumentClose();
			});
	};
	const handleAddAdditionalDocumentChange = (e) => {
		console.log("changeee");
		setAdditionalDocumentInformation({ ...additionalDocumentInformation, [e.target.name]: e.target.value });
	};
	const updateTenantStatus = (contact, propertyId, former) => {
		console.log(`update tenant status: ${contact.email}`);

		const payload = {
			email: contact.email,
			former: !former,
			propertyId: propertyId,
		};
		console.log(payload);
		dispatch(updateTenantFormerAsync(payload)).then((r) => {
			if (r.error?.message)
				toast({
					title: "Error encountered",
					description: r.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			else {
				toast({
					title: "Success!",
					description: "Update successful",
					status: "success",
					duration: 2000,
					isClosable: true,
				});
			}
		});
	};

	const addContact = () => {
		console.log(contactInformation);
		const errors = addLeaseContactVerifier(contactInformation);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addLeaseContactAsync(contactInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Tenant Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setContactInformation(initialContactInformation);
				onCloseAddContact();
			});
	};
	const addTenant = () => {
		console.log(tenantInformation);
		const errors = addTenantVerifier(tenantInformation);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addTenantAsync(tenantInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Tenant Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setTenantInformation(initialTenantInformation);
				onCloseAddTenant();
			});
	};
	const onMediaDrop = async (acceptedFiles) => {
		const media = [];
		console.log(acceptedFiles);
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			const doc = { documentData: _arrayBufferToBase64(buffer), documentName, contentType };
			console.log(doc);
			media.push(doc);
		}
		setReportInformation({ ...reportInformation, media });
	};
	const onReportDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const buffer = await acceptedFiles[0].arrayBuffer();
		const documentName = acceptedFiles[0].name;
		const contentType = acceptedFiles[0].type;
		setReportInformation({ ...reportInformation, documentData: _arrayBufferToBase64(buffer), documentName, contentType });
	};
	const addReport = () => {
		console.log(reportInformation);
		const errors = addDocumentVerifier(reportInformation);
		console.log(errors);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addReportAsync(reportInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Update successful",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onReportClose();
				setReportInformation(initialReportInformation);
			});
	};
	const handleChangeTenantInformation = (e) => {
		console.log("changeee");
		setTenantInformation({ ...tenantInformation, [e.target.name]: e.target.value });
	};
	const handleChangeContactInformation = (e) => {
		console.log("changeee");
		setContactInformation({ ...contactInformation, [e.target.name]: e.target.value });
	};
	const handleChange = (e) => {
		console.log("changeee");
		setDocumentInformation({ ...documentInformation, [e.target.name]: e.target.value });
	};
	const handleCloseAdditionalDocumentModal = () => {
		onAdditionalDocumentClose();
		setError({});
		setAdditionalDocumentInformation(initialAdditionalDocumentInformation);
	};
	const renderAddAdditionalDocumentModal = () => (
		<PropertyModalShell
			isOpen={isAdditionalDocumentOpen}
			onClose={handleCloseAdditionalDocumentModal}
			size="4xl"
			icon={FiFileText}
			title={t("property.addAdditionalDocument")}
			subtitle={t("property.addAdditionalDocumentSubtitle")}
			footer={
				<ModalFooterActions onCancel={handleCloseAdditionalDocumentModal} onConfirm={addAdditionalDocument} confirmLabel={t("misc.add")} />
			}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.documentInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("table.signatureDate")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleAddAdditionalDocumentChange}
										isInvalid={error?.signatureDate}
										type="date"
										name="signatureDate"
										borderRadius="md"
									/>
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("table.startingDate")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleAddAdditionalDocumentChange}
										isInvalid={error?.startingDate}
										type="date"
										name="startingDate"
										borderRadius="md"
									/>
								</FormControl>
							</SimpleGrid>
							<FormControl>
								<ModalFieldLabel icon={FiCalendar}>{t("table.expirationDate")}</ModalFieldLabel>
								<Input
									size="sm"
									onChange={handleAddAdditionalDocumentChange}
									type="date"
									name="expirationDate"
									isInvalid={error?.expirationDate}
									borderRadius="md"
								/>
							</FormControl>
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiFileText}>{t("property.documentNumber")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleAddAdditionalDocumentChange}
										type="text"
										name="documentNumber"
										placeholder={t("property.documentNumberPlaceholder")}
										isInvalid={error?.documentNumber}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiFileText}>{t("table.type")}</ModalFieldLabel>
									<Select
										size="sm"
										name="documentType"
										placeholder={t("table.type")}
										onChange={handleAddAdditionalDocumentChange}
										isInvalid={error?.documentType}
										borderRadius="md"
									>
										{additionalLeaseDocumentTypes.map((d, index) => (
											<option key={index} value={d.id}>
												{d.label}
											</option>
										))}
									</Select>
								</FormControl>
							</SimpleGrid>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.descriptionLabel")}</ModalFieldLabel>
								<Textarea
									size="sm"
									onChange={handleAddAdditionalDocumentChange}
									name="description"
									placeholder={t("property.descriptionPlaceholder")}
									isInvalid={error?.description}
									rows={3}
									borderRadius="md"
								/>
							</FormControl>
							<Checkbox
								name="active"
								onChange={(e) => {
									setAdditionalDocumentInformation({ ...additionalDocumentInformation, active: e.target.checked });
								}}
								isChecked={additionalDocumentInformation.active}
							>
								<Text fontSize="sm">{t("property.isActiveDocument")}</Text>
							</Checkbox>
						</VStack>
					</PropertyModalSection>
				</VStack>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiUpload} title={t("property.documentUpload")}>
						<Dropzone
							onDrop={onAdditionalDocumentDrop}
							accept={{
								"application/pdf": [".pdf"],
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
								"application/msword": [".doc"],
							}}
							description={t("property.uploadPdfDocDocx")}
							maxFiles={1}
						/>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	const handleCloseAddTenantModal = () => {
		onCloseAddTenant();
		setError({});
		setTenantInformation(initialTenantInformation);
		if (isExistingTenantOpen) {
			onExistingTenantToggle();
		}
	};
	const renderAddTenantModal = () => (
		<PropertyModalShell
			isOpen={isOpenAddTenant}
			onClose={handleCloseAddTenantModal}
			size="4xl"
			icon={FiUser}
			title={t("property.addTenant")}
			subtitle={t("property.addTenantSubtitle")}
			footer={
				<ModalFooterActions
					onCancel={handleCloseAddTenantModal}
					onConfirm={addTenant}
					isDisabled={!tenantInformation.name || !tenantInformation.email}
					confirmLabel={isExistingTenantOpen ? t("property.addSelectedTenant") : t("property.addNewTenant")}
				/>
			}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<PropertyModalSection icon={FiUsers} title={t("property.tenantType")}>
					<HStack spacing={2} justify="center">
						<Button
							size="sm"
							variant={!isExistingTenantOpen ? "solid" : "outline"}
							colorScheme="blue"
							leftIcon={<Icon as={FiUser} />}
							onClick={() => {
								if (isExistingTenantOpen) {
									setTenantInformation(initialTenantInformation);
									onExistingTenantToggle();
								}
							}}
							flex={1}
							maxW="200px"
						>
							{t("property.newTenant")}
						</Button>
						<Button
							size="sm"
							variant={isExistingTenantOpen ? "solid" : "outline"}
							colorScheme="blue"
							leftIcon={<Icon as={FiSearch} />}
							onClick={() => {
								if (!isExistingTenantOpen) {
									setTenantInformation(initialTenantInformation);
									onExistingTenantToggle();
								}
							}}
							flex={1}
							maxW="200px"
						>
							{t("property.existingTenant")}
						</Button>
					</HStack>
				</PropertyModalSection>

				<Box position="relative" minH="280px">
					<Box
						position="absolute"
						top={0}
						left={0}
						right={0}
						opacity={isExistingTenantOpen ? 1 : 0}
						visibility={isExistingTenantOpen ? "visible" : "hidden"}
						transition="opacity 0.2s ease-in-out, visibility 0.2s ease-in-out"
					>
						<PropertyModalSection icon={FiSearch} title={t("property.selectExistingTenant")}>
							<VStack spacing={3} align="stretch">
								<FormControl>
									<ModalFieldLabel icon={FiSearch}>{t("property.searchSelectTenant")}</ModalFieldLabel>
									<ChakraSelect
										options={uniqueTenantsAndFormerTenants.map((tenant) => ({
											label: `${tenant.name} (${tenant.email})`,
											value: tenant,
											data: tenant,
										}))}
										onChange={(selectedOption) => {
											if (selectedOption) {
												const tenant = selectedOption.value;
												setTenantInformation({
													...tenantInformation,
													existing: true,
													name: tenant.name,
													email: tenant.email,
													phoneNumber: tenant.phoneNumber,
												});
											}
										}}
										placeholder={t("property.searchExistingTenantsPlaceholder")}
										isSearchable
										noOptionsMessage={() => t("property.noTenantsFound")}
										loadingMessage={() => t("property.loadingTenants")}
										chakraStyles={{
											control: (provided) => ({
												...provided,
												borderRadius: "8px",
												borderColor: borderColor,
											}),
											option: (provided, state) => ({
												...provided,
												backgroundColor: state.isSelected ? "blue.500" : state.isFocused ? "gray.100" : "transparent",
												color: state.isSelected ? "white" : "inherit",
											}),
										}}
									/>
								</FormControl>
								{tenantInformation.name && (
									<Alert status="success" borderRadius="md" size="sm">
										<AlertIcon />
										<VStack align="start" spacing={1}>
											<AlertTitle fontSize="sm">{t("property.selectedTenant")}</AlertTitle>
											<AlertDescription fontSize="sm">
												{tenantInformation.name} - {tenantInformation.email}
											</AlertDescription>
										</VStack>
									</Alert>
								)}
							</VStack>
						</PropertyModalSection>
					</Box>

					<Box
						position="absolute"
						top={0}
						left={0}
						right={0}
						opacity={!isExistingTenantOpen ? 1 : 0}
						visibility={!isExistingTenantOpen ? "visible" : "hidden"}
						transition="opacity 0.2s ease-in-out, visibility 0.2s ease-in-out"
					>
						<PropertyModalSection icon={FiUser} title={t("property.newTenantInformation")}>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								<FormControl>
									<ModalFieldLabel icon={FiUser}>{t("property.fullName")}</ModalFieldLabel>
									<Input
										size="sm"
										isInvalid={error?.name}
										type="text"
										name="name"
										placeholder={t("property.namePlaceholder")}
										value={tenantInformation.name}
										onChange={handleChangeTenantInformation}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiMail}>{t("property.emailAddress")}</ModalFieldLabel>
									<Input
										size="sm"
										type="email"
										isInvalid={error?.email}
										name="email"
										placeholder={t("property.emailPlaceholder")}
										value={tenantInformation.email}
										onChange={handleChangeTenantInformation}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl gridColumn={{ base: "1", md: "1 / -1" }}>
									<ModalFieldLabel icon={FiPhone}>{t("property.phoneNumber")}</ModalFieldLabel>
									<Input
										size="sm"
										type="tel"
										isInvalid={error?.phoneNumber}
										name="phoneNumber"
										placeholder={t("property.phoneNumberPlaceholder")}
										value={tenantInformation.phoneNumber}
										onChange={handleChangeTenantInformation}
										borderRadius="md"
									/>
								</FormControl>
							</SimpleGrid>
						</PropertyModalSection>
					</Box>
				</Box>
			</VStack>
		</PropertyModalShell>
	);
	const handleCloseAddContactModal = () => {
		onCloseAddContact();
		setError({});
		setContactInformation(initialContactInformation);
	};
	const renderAddContactModal = () => (
		<PropertyModalShell
			isOpen={isOpenAddContact}
			onClose={handleCloseAddContactModal}
			size="3xl"
			icon={FiUser}
			title={t("property.addContact")}
			subtitle={t("property.addContactSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseAddContactModal} onConfirm={addContact} confirmLabel={t("misc.save")} />}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<Alert status="info" borderRadius="md" size="sm">
					<AlertIcon />
					<VStack align="start" spacing={1}>
						<AlertTitle fontSize="sm">{t("property.contactInformation")}</AlertTitle>
						<AlertDescription fontSize="sm">{t("property.contactInformationAlert")}</AlertDescription>
					</VStack>
				</Alert>
				<PropertyModalSection icon={FiInfo} title={t("property.contactDetails")}>
					<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
						<FormControl>
							<ModalFieldLabel icon={FiUser}>{t("property.name")}</ModalFieldLabel>
							<Input
								size="sm"
								isInvalid={error?.name}
								type="text"
								name="name"
								placeholder={t("property.namePlaceholder")}
								value={contactInformation.name}
								onChange={handleChangeContactInformation}
								borderRadius="md"
							/>
						</FormControl>
						<FormControl>
							<ModalFieldLabel icon={FiMail}>{t("property.email")}</ModalFieldLabel>
							<Input
								size="sm"
								type="text"
								isInvalid={error?.email}
								name="email"
								placeholder={t("property.emailPlaceholder")}
								value={contactInformation.email}
								onChange={handleChangeContactInformation}
								borderRadius="md"
							/>
						</FormControl>
						<FormControl>
							<ModalFieldLabel icon={FiPhone}>{t("property.phoneNumber")}</ModalFieldLabel>
							<Input
								size="sm"
								type="number"
								isInvalid={error?.phoneNumber}
								name="phoneNumber"
								placeholder={t("property.phoneNumberPlaceholder")}
								value={contactInformation.phoneNumber}
								onChange={handleChangeContactInformation}
								borderRadius="md"
							/>
						</FormControl>
						<FormControl>
							<ModalFieldLabel icon={FiFileText}>{t("property.type")}</ModalFieldLabel>
							<Select
								size="sm"
								isInvalid={error?.contactType}
								name="contactType"
								placeholder={t("property.type")}
								value={contactInformation.contactType}
								onChange={handleChangeContactInformation}
								borderRadius="md"
							>
								{contactTypes.map((type, index) => (
									<option key={index} value={type.id}>
										{type.label}
									</option>
								))}
							</Select>
						</FormControl>
					</SimpleGrid>
				</PropertyModalSection>
			</VStack>
		</PropertyModalShell>
	);
	const handleCloseLeaseModal = () => {
		onClose();
		setError({});
		setDocumentInformation(initialDocumentInformation);
	};
	const renderAddDocumentModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseLeaseModal}
			size="5xl"
			icon={FiFileText}
			title={t("property.addLease")}
			subtitle={t("property.addLeaseSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseLeaseModal} onConfirm={addDocument} confirmLabel={t("misc.add")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.documentInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("table.signatureDate")}</ModalFieldLabel>
									<Input size="sm" onChange={handleChange} isInvalid={error?.signatureDate} type="date" name="signatureDate" borderRadius="md" />
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("table.startingDate")}</ModalFieldLabel>
									<Input size="sm" onChange={handleChange} isInvalid={error?.startingDate} type="date" name="startingDate" borderRadius="md" />
								</FormControl>
							</SimpleGrid>
							<FormControl>
								<ModalFieldLabel icon={FiCalendar}>{t("table.expirationDate")}</ModalFieldLabel>
								<Input size="sm" onChange={handleChange} type="date" name="expirationDate" isInvalid={error?.expirationDate} borderRadius="md" />
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.documentNumber")}</ModalFieldLabel>
								<Input
									size="sm"
									onChange={handleChange}
									type="text"
									name="documentNumber"
									placeholder={t("property.documentNumberPlaceholder")}
									isInvalid={error?.documentNumber}
									borderRadius="md"
								/>
							</FormControl>
						</VStack>
					</PropertyModalSection>

					<PropertyModalSection icon={FiTrendingUp} title={t("property.financialInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiHash}>{t("property.amount")}</ModalFieldLabel>
									<Input
										size="sm"
										isInvalid={error?.amount}
										onChange={handleChange}
										type="number"
										name="amount"
										placeholder={t("property.amountPlaceholder")}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiGlobe}>{t("property.currency")}</ModalFieldLabel>
									<Select size="sm" name="currency" placeholder={t("property.currency")} onChange={handleChange} isInvalid={error?.currency} borderRadius="md">
										<option value="eur">EUR</option>
										<option value="ron">RON</option>
									</Select>
								</FormControl>
							</SimpleGrid>
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("property.firstRentDay")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleChange}
										type="number"
										name="firstRentDay"
										placeholder={t("property.firstDay")}
										isInvalid={error?.firstRentDay}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("property.lastRentDay")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleChange}
										isInvalid={error?.lastRentDay}
										type="number"
										name="lastRentDay"
										placeholder={t("property.lastDay")}
										borderRadius="md"
									/>
								</FormControl>
							</SimpleGrid>
						</VStack>
					</PropertyModalSection>
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiGlobe} title={t("property.exchangeRateConfiguration")}>
						<VStack spacing={3} align="stretch">
							<FormControl>
								<ModalFieldLabel icon={FiGlobe}>{t("property.rentExchangeRate")}</ModalFieldLabel>
								<RadioGroup
									name="exchangeRateType"
									value={documentInformation.exchangeRateType}
									onChange={(e) => {
										console.log(e);
										if (e === "fixedExchangeRate") setDocumentInformation({ ...documentInformation, exchangeRateType: e, fixedExchangeRate: 4.9 });
										else {
											const tempRent = { ...documentInformation };
											delete tempRent.fixedExchangeRate;
											setDocumentInformation({ ...tempRent, exchangeRateType: e });
										}
									}}
								>
									<VStack spacing={2} align="start">
										<Radio value="bnr" size="sm">
											BNR din ziua platii
										</Radio>
										<Radio value="bnr2" size="sm">
											BNR+2% din ziua platii
										</Radio>
										<Radio value="fixedExchangeRate" size="sm">
											Curs fix
										</Radio>
									</VStack>
								</RadioGroup>
							</FormControl>
							<FormControl hidden={documentInformation.exchangeRateType !== "fixedExchangeRate"}>
								<ModalFieldLabel icon={FiGlobe}>{t("property.fixedExchangeRate")}</ModalFieldLabel>
								<Input
									size="sm"
									isDisabled={documentInformation.exchangeRateType !== "fixedExchangeRate"}
									maxW={24}
									type="number"
									name="fixedExchangeRate"
									onChange={handleChange}
									isInvalid={error?.fixedExchangeRate}
									value={documentInformation.fixedExchangeRate}
									placeholder={t("property.enterExchangeRate")}
									borderRadius="md"
								/>
							</FormControl>
						</VStack>
					</PropertyModalSection>

					<PropertyModalSection icon={FiSettings} title={t("property.administrationFeeOptional")}>
						<SimpleGrid columns={2} spacing={4} w="full">
							<FormControl>
								<ModalFieldLabel icon={FiHash}>{t("property.amount")}</ModalFieldLabel>
								<Input
									size="sm"
									maxW={24}
									isInvalid={error?.fixedAdministrationFeeAmount}
									name="fixedAdministrationFeeAmount"
									placeholder={t("property.amountPlaceholder")}
									onChange={handleChange}
									type="number"
									borderRadius="md"
								/>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiGlobe}>{t("property.currency")}</ModalFieldLabel>
								<Select size="sm" maxW={24} name="fixedAdministrationFeeCurrency" onChange={handleChange} borderRadius="md">
									<option value="">{t("property.currency")}</option>
									<option value="eur">EUR</option>
									<option value="ron">RON</option>
								</Select>
							</FormControl>
						</SimpleGrid>
					</PropertyModalSection>

					<PropertyModalSection icon={FiUpload} title={t("property.leaseDocument")}>
						<Dropzone
							onDrop={onDocumentDrop}
							accept={{
								"application/pdf": [".pdf"],
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
								"application/msword": [".doc"],
							}}
							description={t("property.uploadPdfDocDocx")}
							maxFiles={1}
						/>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);
	const handleCloseReportModal = () => {
		onReportClose();
	};
	const renderAddReportModal = () => (
		<PropertyModalShell
			isOpen={isReportOpen}
			onClose={handleCloseReportModal}
			size="4xl"
			icon={FiFile}
			title={t("property.addReport")}
			subtitle={t("property.addReportSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseReportModal} onConfirm={addReport} confirmLabel={t("misc.add")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.reportInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("table.addedDate")}</ModalFieldLabel>
									<Input
										size="sm"
										isInvalid={error?.addedDate}
										onChange={handleAddReportChange}
										type="date"
										value={reportInformation.addedDate}
										name="addedDate"
										borderRadius="md"
									/>
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiFileText}>{t("property.documentNumber")}</ModalFieldLabel>
									<Input
										size="sm"
										isInvalid={error?.documentNumber}
										onChange={handleAddReportChange}
										type="text"
										name="documentNumber"
										placeholder={t("property.documentNumberPlaceholder")}
										borderRadius="md"
									/>
								</FormControl>
							</SimpleGrid>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.type")}</ModalFieldLabel>
								<Select
									size="sm"
									isInvalid={error?.documentType}
									onChange={handleAddReportChange}
									name="documentType"
									placeholder={t("property.type")}
									borderRadius="md"
								>
									{reportTypes.map((d, index) => (
										<option key={index} value={d.id}>
											{d.label}
										</option>
									))}
								</Select>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.descriptionLabel")}</ModalFieldLabel>
								<Textarea
									size="sm"
									isInvalid={error?.description}
									onChange={handleAddReportChange}
									name="description"
									placeholder={t("property.descriptionPlaceholder")}
									rows={3}
									borderRadius="md"
								/>
							</FormControl>
						</VStack>
					</PropertyModalSection>
				</VStack>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiUpload} title={t("property.reportDocument")}>
						<Dropzone
							onDrop={onReportDrop}
							accept={{
								"application/pdf": [".pdf"],
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
								"application/msword": [".doc"],
							}}
							description={t("property.uploadPdfDocDocx")}
							maxFiles={1}
							error={error?.documentData}
						/>
					</PropertyModalSection>
					<PropertyModalSection icon={FiUpload} title={t("property.mediaFiles")}>
						<Dropzone
							onDrop={onMediaDrop}
							accept={{ "image/*": [], "video/*": [] }}
							description={t("property.uploadImagesOrVideos")}
							error={error?.media}
						/>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);
	return (
		<Stack>
			<Wrap>
				<WrapItem>
					<Box>
						<Tooltip
							label="Add billing information to the agency and owner"
							isDisabled={Object.keys(ownerBillingInformation).length !== 0 || Object.keys(agencyBillingInformation).length !== 0}
						>
							<Button
								onClick={onOpen}
								colorScheme="blue"
								isDisabled={Object.keys(ownerBillingInformation).length === 0 || Object.keys(agencyBillingInformation).length === 0 || isArchived}
							>
								{t("property.addLease")}
							</Button>
						</Tooltip>

						{renderAddDocumentModal()}
					</Box>
				</WrapItem>
				<WrapItem>
					<Box>
						<Button onClick={onAdditionalDocumentOpen} colorScheme="blue" isDisabled={isArchived}>
							{t("property.addAdditionalDocument")}
						</Button>
						{renderAddAdditionalDocumentModal()}
					</Box>
				</WrapItem>
				<WrapItem>
					<Box>
						<Tooltip label="Add Administration Contract to enable" isDisabled={getPropertyStatus(property) !== "inactive"}>
							<Button colorScheme="green" onClick={onOpenAddTenant} isDisabled={getPropertyStatus(property) === "inactive" || isArchived}>
								{t("property.addTenant")}
							</Button>
						</Tooltip>

						{renderAddTenantModal()}
					</Box>
				</WrapItem>
				<WrapItem>
					<Box>
						<Button colorScheme="blue" onClick={onOpenAddContact} isDisabled={isArchived}>
							{t("property.addContact")}
						</Button>
						{renderAddContactModal()}
					</Box>
				</WrapItem>
				<WrapItem>
					<Button onClick={onReportOpen} colorScheme="blue" width={"fit-content"} isDisabled={isArchived}>
						{t("property.addReport")}
					</Button>
					{renderAddReportModal()}
				</WrapItem>
			</Wrap>
			<Box>
				<Checkbox
					isDisabled={isArchived}
					isChecked={property.owner?.handlesOwnSanitationDeclaration}
					onChange={(e) => {
						console.log("handles own sanitation declaration:", e.target.checked);
						dispatch(updateOwnerAsync({ type: "handlesOwn", handlesOwnSanitationDeclaration: e.target.checked, ownerEmail: property.owner.email }));
					}}
				>
					{t("property.ownerHandlesSanitationDeclaration")}
				</Checkbox>
			</Box>
			<Wrap>
				{property.tenants
					.filter((t) => !t.former)
					.map((c, index) => (
						<WrapItem key={index}>
							<TenantPopover contact={c} propertyId={property.id} handleFormer={updateTenantStatus} />
						</WrapItem>
					))}

				{property.leaseContacts
					?.filter((c) => !c.former)
					.map((c, index) => (
						<WrapItem key={index}>
							<ContactPopover contact={c} property={property} />
						</WrapItem>
					))}
			</Wrap>
			<Divider />
			<Box hidden={!formerTenants.length}>
				<Text fontWeight={"bold"}>{t("property.formerTenants")}:</Text>
				<Wrap>
					{formerTenants.map((c, index) => (
						<WrapItem key={index}>
							<TenantPopover contact={c} propertyId={property.id} former={true} handleFormer={updateTenantStatus} />
						</WrapItem>
					))}
				</Wrap>
			</Box>
			<Box hidden={!formerContacts.length}>
				<Text fontWeight={"bold"}>{t("property.formerContacts")}:</Text>
				<Wrap>
					{formerContacts.map((c, index) => (
						<WrapItem key={index}>
							<ContactPopover contact={c} propertyId={property.id} />
						</WrapItem>
					))}
				</Wrap>
			</Box>
			<Divider />
			<Box overflowX="auto">
				<Text fontWeight={"bold"} fontSize={"large"} margin={"20px 0px 20px 0px"}>
					{t("property.reports")}
				</Text>
				<Table variant="simple" size="sm">
					<Thead>
						<Tr my=".8rem" pl="0px">
							<Th pl="0px">{t("table.addedDate")}</Th>
							<Th>{t("table.name")}</Th>
							<Th>{t("table.type")}</Th>
							<Th>{t("table.description")}</Th>
							<Th>{t("table.number")}</Th>
							<Th></Th>
							<Th></Th>
						</Tr>
					</Thead>
					<Tbody>
						{reports.length !== 0 ? (
							reports.map((row, index) => {
								return <ReportsTableRow key={index} row={row} propertyId={property.id} />;
							})
						) : (
							<Tr>
								<Td>{t("table.noData")}</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</Box>
			<Box overflowX="auto">
				<Text fontWeight={"bold"} fontSize={"large"} margin={"20px 0px 20px 0px"}>
					{t("property.lease")}
				</Text>

				<Table variant="simple" size="sm">
					<Thead>
						<Tr my=".8rem" pl="0px" whiteSpace={"nowrap"}>
							<Th pl="0px">{t("table.addedDate")}</Th>
							<Th>{t("table.name")}</Th>
							<Th>{t("table.type")}</Th>
							<Th>{t("table.number")}</Th>
							<Th>{t("table.signatureDate")}</Th>
							<Th>{t("table.startingDate")}</Th>
							<Th>{t("table.expirationDate")}</Th>
							<Th></Th>
						</Tr>
					</Thead>
					<Tbody>
						{documents.length !== 0 ? (
							<>
								{documents
									.filter((d) => d.active)
									.map((row, index) => {
										return <LeaseTableRow key={index} row={row} propertyId={property.id} payments={property.payments} />;
									})}
								{documents
									.filter((d) => !d.active)
									.map((row, index) => {
										return <LeaseTableRow key={index} row={row} propertyId={property.id} payments={property.payments} />;
									})}
							</>
						) : (
							<Tr>
								<Td>{t("table.noData")}</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</Box>
		</Stack>
	);
}
